'use client';

import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import clsx from 'clsx';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TableVirtuoso } from 'react-virtuoso';

import {
  InlineButton,
  ChevronIcon,
  Popover,
  SecondaryButton,
} from '../../atomic-atoms';
import { SmartInput } from '../../atomic-molecules';
import { OptionsMenu } from '../OptionsMenu';

import styles from './Table.module.scss';
import { useTableBaseState, type RowItem } from './useTableBaseState';

import type { ColumnDef, TableBaseImplProps } from './TableBaseImpl.types';
import type { ComponentProps, CSSProperties, HTMLAttributes } from 'react';

const DEFAULT_COLUMN_WIDTH = 160;
const SELECTION_COLUMN_WIDTH = 48;
const EXPAND_COLUMN_WIDTH = 15;
const OVERLAY_TOP_OFFSET = 1;

function findScrollParent(
  element: HTMLElement | null,
): HTMLElement | Window | null {
  if (!element || typeof window === 'undefined') {
    return null;
  }

  let current: HTMLElement | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    const canScrollY =
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'overlay') &&
      current.scrollHeight > current.clientHeight + 1;

    if (canScrollY) {
      return current;
    }

    current = current.parentElement;
  }

  return window;
}

function escapeQueryValue(value: string) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/["\\]/g, '\\$&');
}

function getColumnWidth<T>(
  column: ColumnDef<T>,
  columnSizing: Record<string, number>,
) {
  return (
    columnSizing[column.id] ??
    column.width ??
    column.minWidth ??
    DEFAULT_COLUMN_WIDTH
  );
}

function TableSelectionCheckbox({
  name,
  checked,
  onChange,
  readOnly,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  readOnly?: boolean;
}) {
  return (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          name={name}
          checked={checked}
          disabled={readOnly}
          onChange={() => onChange()}
        />
      }
      label=""
    />
  );
}

/**
 * ! Internal base table implementation. Prefer `PaginatedTable` or `InfiniteTable`.
 *
 * * TableBaseImpl supports controlled and uncontrolled state for key table behaviors.
 *
 * * Controlled mode:
 * * - Pagination: pass `page`, `pageSize`, and handle `onPageChange`/`onPageSizeChange`.
 * * - Sorting: pass `sort` and handle `onSortChange`.
 * * - Selection: pass `selectedRowIds` and handle `onSelectionChange`.
 * * - Columns: pass `columnVisibility`, `columnOrder`, `columnSizing`, `columnPinning`
 * *   and handle the corresponding `on*Change` callbacks.
 * * - Sections: pass `sectionExpandedIds` and handle `onSectionExpandedChange`.
 *
 * * Uncontrolled mode:
 * * - Omit the controlled values and use the `default*` props for initial state.
 *
 * * Notes:
 * * - Classic pagination will slice locally unless `totalRows` is provided (server mode).
 * * - Infinite mode uses `onLoadMore` for fetch triggers and ignores local paging.
 */
/** @internal */
export function TableBaseImpl<T extends Record<string, any>>({
  tableId,
  columns,
  rows,
  getRowId,
  getSubRowId,
  paginationMode = 'classic',
  totalRows,
  showPagination = true,
  onManageColumns,
  renderRowSubRows,
  renderRowSubComponent,
  renderRowSubCells,
  renderRowSubHeader,
  renderRowSubColumns,
  isRowCollapsible,
  density = 'standard',
  rowHeight = 52.5,
  isLoading = false,
  skeletonRowCount,
  emptyMessage = 'No data available.',
  className,
  virtualizedHeight = 420,
  onLoadMore,
  hasMore = false,
  onRowClick,
  stickyExpandedRows = false,
  selectMultiple = false,
  selectedSubRowIds: controlledSelectedSubRowIds,
  defaultSelectedSubRowIds,
  onSubRowSelectionChange,
  selectSubRows = false,
  expandAll,
  ...stateProps
}: TableBaseImplProps<T>) {
  const hasSubRowContent = Boolean(
    renderRowSubRows || renderRowSubComponent || renderRowSubCells,
  );
  const enableCollapsibleRows = hasSubRowContent;

  const hasSubRowsForRow = useCallback(
    (row: T) => {
      if (renderRowSubRows) {
        return (renderRowSubRows(row) ?? []).length > 0;
      }
      return hasSubRowContent;
    },
    [hasSubRowContent, renderRowSubRows],
  );

  const {
    orderedColumns,
    visibleColumns,
    columnSizing,
    columnVisibility,
    columnPinning,
    page,
    pageSize,
    sort,
    selectionMode,
    selectedRowIds,
    selectedSubRowIds,
    rowItems,
    totalCount,
    expandedRowIds,
    setPage,
    setPageSize,
    setSort,
    setSelectedRowIds,
    setColumnVisibility,
    setColumnPinning,
    setColumnSizing,
    toggleRowSelection,
    toggleSubRowSelection,
    toggleAllRows,
    toggleAllSubRows,
    toggleRowExpanded,
    setExpandedRowIds,
    toggleSectionExpanded,
    getColumnPin,
  } = useTableBaseState({
    columns,
    rows,
    getRowId,
    getSubRowId,
    paginationMode,
    totalRows,
    showPagination,
    collapsibleRows: enableCollapsibleRows,
    renderRowSubRows,
    renderRowSubCells,
    renderRowSubComponent,
    selectedSubRowIds: controlledSelectedSubRowIds,
    defaultSelectedSubRowIds,
    onSubRowSelectionChange,
    ...stateProps,
  });

  const isSelectionVisible = selectionMode !== 'none' && selectMultiple;
  const isSubSelectionVisible =
    selectionMode !== 'none' &&
    selectSubRows &&
    Boolean(onSubRowSelectionChange);
  const extraCellCount =
    (isSelectionVisible ? 1 : 0) + (enableCollapsibleRows ? 1 : 0);

  const dataRows = useMemo(
    () =>
      rowItems
        .filter((item) => item.type === 'data')
        .map((item) => ({ rowId: item.rowId, row: item.row })),
    [rowItems],
  );

  const rowIds = useMemo(() => dataRows.map((item) => item.rowId), [dataRows]);
  const stickyHeaderOverlayEnabled = paginationMode === 'classic';
  const stickyExpandedRowsEnabled =
    stickyExpandedRows && paginationMode === 'classic';
  const rowDataById = useMemo(() => {
    const map: Record<
      string,
      { row: T; sectionId?: string; rowIndex: number }
    > = {};
    let rowIndex = 0;
    rowItems.forEach((item) => {
      if (item.type !== 'data') {
        return;
      }
      map[item.rowId] = { row: item.row, sectionId: item.sectionId, rowIndex };
      rowIndex += 1;
    });
    return map;
  }, [rowItems]);

  const expandableRowIds = useMemo(
    () =>
      dataRows
        .filter((item) => hasSubRowsForRow(item.row))
        .map((item) => item.rowId),
    [dataRows, hasSubRowsForRow],
  );

  const subRowItemsByParentId = useMemo(() => {
    const map: Record<string, { row: T; subRowId: string }[]> = {};
    rowItems.forEach((item) => {
      if (item.type !== 'detail') {
        return;
      }
      const list = map[item.parentRowId] ?? [];
      list.push({ row: item.row, subRowId: item.subRowId });
      map[item.parentRowId] = list;
    });
    return map;
  }, [rowItems]);

  const sectionById = useMemo(() => {
    const sections = stateProps.sections ?? [];
    return sections.reduce<Record<string, (typeof sections)[number]>>(
      (acc, section) => {
        acc[section.id] = section;
        return acc;
      },
      {},
    );
  }, [stateProps.sections]);

  const allSelected =
    selectionMode === 'multiple' &&
    rowIds.length > 0 &&
    rowIds.every((id) => selectedRowIds.includes(id));

  const lastExpandAllRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (!enableCollapsibleRows || expandAll === undefined) {
      lastExpandAllRef.current = expandAll;
      return;
    }

    if (lastExpandAllRef.current === expandAll) {
      return;
    }

    lastExpandAllRef.current = expandAll;

    if (expandAll) {
      setExpandedRowIds(expandableRowIds);
      return;
    }

    setExpandedRowIds([]);
  }, [enableCollapsibleRows, expandAll, expandableRowIds, setExpandedRowIds]);

  const densityClass =
    density === 'compact'
      ? styles.densityCompact
      : density === 'comfortable'
        ? styles.densityComfortable
        : undefined;

  const resolvedRowHeight =
    typeof rowHeight === 'number' ? `${rowHeight}px` : rowHeight;

  const tableWrapperStyle = useMemo(
    () =>
      ({
        '--nn-table-row-height': resolvedRowHeight,
      }) as CSSProperties,
    [resolvedRowHeight],
  );

  const getVisibleColumnsFor = useCallback(
    (columnsList: ColumnDef<T>[]) => {
      const filtered = columnsList.filter(
        (col) => columnVisibility[col.id] !== false,
      );
      const leftPinned = filtered.filter(
        (col) => getColumnPin(col.id) === 'left',
      );
      const rightPinned = filtered.filter(
        (col) => getColumnPin(col.id) === 'right',
      );
      const center = filtered.filter((col) => getColumnPin(col.id) === 'none');
      return [...leftPinned, ...center, ...rightPinned];
    },
    [columnVisibility, getColumnPin],
  );

  const getColumnOffsetsFor = useCallback(
    (columnsList: ColumnDef<T>[]) => {
      const leftOffsets: Record<string, number> = {};
      const rightOffsets: Record<string, number> = {};

      let left = 0;
      columnsList.forEach((col) => {
        if (getColumnPin(col.id) === 'left') {
          leftOffsets[col.id] = left;
          left += getColumnWidth(col, columnSizing);
        }
      });

      let right = 0;
      [...columnsList].reverse().forEach((col) => {
        if (getColumnPin(col.id) === 'right') {
          rightOffsets[col.id] = right;
          right += getColumnWidth(col, columnSizing);
        }
      });

      return { leftOffsets, rightOffsets };
    },
    [columnSizing, getColumnPin],
  );

  const columnOffsets = useMemo(
    () => getColumnOffsetsFor(visibleColumns),
    [getColumnOffsetsFor, visibleColumns],
  );

  const getSectionColumns = useCallback(
    (sectionId?: string) => {
      if (!sectionId) {
        return visibleColumns;
      }
      const section = sectionById[sectionId];
      if (!section?.columns || section.columns.length === 0) {
        return visibleColumns;
      }
      return getVisibleColumnsFor(section.columns);
    },
    [getVisibleColumnsFor, sectionById, visibleColumns],
  );

  const getColSpanForSection = useCallback(
    (sectionId?: string) =>
      getSectionColumns(sectionId).length + extraCellCount,
    [extraCellCount, getSectionColumns],
  );

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const mainTableRef = useRef<HTMLTableElement | null>(null);
  const stickyRafRef = useRef<number | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [manageAnchorEl, setManageAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [manageMenuId, setManageMenuId] = useState<string | null>(null);
  const manageMenuIdRef = useRef<string | null>(null);
  const manageOpen = Boolean(manageAnchorEl);
  const [overlayScrollLeft, setOverlayScrollLeft] = useState(0);
  const [overlayTableWidth, setOverlayTableWidth] = useState(0);
  const [overlayHeaderHeight, setOverlayHeaderHeight] = useState(0);
  const [overlayTop, setOverlayTop] = useState(0);
  const [overlayLeft, setOverlayLeft] = useState(0);
  const [overlayWidth, setOverlayWidth] = useState(0);
  const [isHeaderOverlayPinned, setIsHeaderOverlayPinned] = useState(false);
  const [activePinnedRowId, setActivePinnedRowId] = useState<string | null>(
    null,
  );
  const [hiddenPinnedSourceRowId, setHiddenPinnedSourceRowId] = useState<
    string | null
  >(null);
  const lastPinnedRowIdRef = useRef<string | null>(null);

  const handleManageColumns = useCallback(
    (menuId: string, anchor?: HTMLElement | null) => {
      manageMenuIdRef.current = menuId;
      setManageMenuId(menuId);
      setOpenMenuId(menuId);
      setManageAnchorEl(anchor ?? menuAnchorEl ?? wrapperRef.current);
      onManageColumns?.();
    },
    [menuAnchorEl, onManageColumns],
  );

  const handleManageClose = useCallback(() => {
    setManageAnchorEl(null);
    setManageMenuId(null);
    setOpenMenuId(null);
    manageMenuIdRef.current = null;
  }, []);

  const resizeState = useRef<{
    columnId: string | null;
    startX: number;
    startWidth: number;
  }>({ columnId: null, startX: 0, startWidth: 0 });

  const startResize = useCallback(
    (column: ColumnDef<T>, event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (column.resizable === false) {
        return;
      }
      resizeState.current = {
        columnId: column.id,
        startX: event.clientX,
        startWidth: getColumnWidth(column, columnSizing),
      };
      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!resizeState.current.columnId) {
          return;
        }
        const delta = moveEvent.clientX - resizeState.current.startX;
        const nextWidth = Math.max(
          column.minWidth ?? 80,
          resizeState.current.startWidth + delta,
        );
        setColumnSizing({
          ...columnSizing,
          [column.id]: nextWidth,
        });
      };
      const handleMouseUp = () => {
        resizeState.current = { columnId: null, startX: 0, startWidth: 0 };
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [columnSizing, setColumnSizing],
  );

  useEffect(() => {
    if (!stickyHeaderOverlayEnabled) {
      lastPinnedRowIdRef.current = null;
      return;
    }

    const container = tableContainerRef.current;
    const mainTable = mainTableRef.current;
    if (!container || !mainTable) {
      return;
    }

    const scrollParent = findScrollParent(container);

    const computeActivePinnedRowId = (
      thresholdTop: number,
      headerPinned: boolean,
    ) => {
      if (!headerPinned || expandedRowIds.length === 0) {
        return null;
      }

      let nextRowId: string | null = null;

      rowItems.forEach((item) => {
        if (item.type !== 'data' || !expandedRowIds.includes(item.rowId)) {
          return;
        }

        const escapedRowId = escapeQueryValue(item.rowId);
        const rowElement = mainTable.querySelector(
          `tr[data-row-type="data"][data-row-id="${escapedRowId}"]`,
        ) as HTMLTableRowElement | null;

        if (!rowElement) {
          return;
        }

        const detailRows = mainTable.querySelectorAll(
          `tr[data-row-type="detail"][data-parent-row-id="${escapedRowId}"]`,
        );
        if (detailRows.length === 0) {
          return;
        }

        const lastDetailRow = detailRows[
          detailRows.length - 1
        ] as HTMLTableRowElement | null;
        if (!lastDetailRow) {
          return;
        }

        const rowTop = rowElement.getBoundingClientRect().top;
        const detailBottom = lastDetailRow.getBoundingClientRect().bottom;

        if (rowTop <= thresholdTop && detailBottom > thresholdTop) {
          nextRowId = item.rowId;
        }
      });

      return nextRowId;
    };

    const syncStickyOverlayState = () => {
      const headerRowElement = mainTable.querySelector('thead tr');
      const headerHeight =
        headerRowElement?.getBoundingClientRect().height ?? 0;
      const tableWidth = mainTable.scrollWidth;
      const scrollLeft = container.scrollLeft;

      const containerRect = container.getBoundingClientRect();
      const isInternalScrollParent = scrollParent === container;
      const scrollParentRect =
        scrollParent && scrollParent !== window
          ? (scrollParent as HTMLElement).getBoundingClientRect()
          : null;
      const viewportTop = scrollParentRect?.top ?? 0;
      const viewportLeft = scrollParentRect?.left ?? 0;

      const isHeaderPinned = isInternalScrollParent
        ? container.scrollTop > 0
        : containerRect.top <= viewportTop &&
          containerRect.bottom > viewportTop + headerHeight;

      const nextOverlayTop = isInternalScrollParent
        ? containerRect.top + OVERLAY_TOP_OFFSET
        : viewportTop + OVERLAY_TOP_OFFSET;
      const nextOverlayLeft = isInternalScrollParent
        ? containerRect.left
        : Math.max(containerRect.left, viewportLeft);
      const nextOverlayWidth = Math.max(
        0,
        Math.min(
          containerRect.right,
          viewportLeft + (scrollParentRect?.width ?? window.innerWidth),
        ) - nextOverlayLeft,
      );

      const thresholdTop = nextOverlayTop + headerHeight;

      const nextPinnedRowId = stickyExpandedRowsEnabled
        ? computeActivePinnedRowId(thresholdTop, isHeaderPinned)
        : null;
      let nextHiddenPinnedSourceRowId: string | null = null;
      const isPinnedRowHandoff = lastPinnedRowIdRef.current !== nextPinnedRowId;

      if (stickyExpandedRowsEnabled && nextPinnedRowId && !isPinnedRowHandoff) {
        const escapedPinnedRowId = escapeQueryValue(nextPinnedRowId);
        const pinnedSourceRowElement = mainTable.querySelector(
          `tr[data-row-type="data"][data-row-id="${escapedPinnedRowId}"]`,
        ) as HTMLTableRowElement | null;

        if (pinnedSourceRowElement) {
          const sourceRect = pinnedSourceRowElement.getBoundingClientRect();
          const overlayRowTop = nextOverlayTop + headerHeight;
          const overlayRowBottom = overlayRowTop + sourceRect.height;
          const overlapsOverlayZone =
            sourceRect.top < overlayRowBottom &&
            sourceRect.bottom > overlayRowTop;

          if (overlapsOverlayZone) {
            nextHiddenPinnedSourceRowId = nextPinnedRowId;
          }
        }
      }

      setOverlayHeaderHeight((current) =>
        current === headerHeight ? current : headerHeight,
      );
      setOverlayTableWidth((current) =>
        current === tableWidth ? current : tableWidth,
      );
      setOverlayScrollLeft((current) =>
        current === scrollLeft ? current : scrollLeft,
      );
      setOverlayTop((current) =>
        current === nextOverlayTop ? current : nextOverlayTop,
      );
      setOverlayLeft((current) =>
        current === nextOverlayLeft ? current : nextOverlayLeft,
      );
      setOverlayWidth((current) =>
        current === nextOverlayWidth ? current : nextOverlayWidth,
      );
      setIsHeaderOverlayPinned((current) =>
        current === isHeaderPinned ? current : isHeaderPinned,
      );
      setActivePinnedRowId((current) =>
        current === nextPinnedRowId ? current : nextPinnedRowId,
      );
      setHiddenPinnedSourceRowId((current) =>
        current === nextHiddenPinnedSourceRowId
          ? current
          : nextHiddenPinnedSourceRowId,
      );
      lastPinnedRowIdRef.current = nextPinnedRowId;
    };

    const scheduleSync = () => {
      if (stickyRafRef.current !== null) {
        cancelAnimationFrame(stickyRafRef.current);
      }
      stickyRafRef.current = requestAnimationFrame(() => {
        stickyRafRef.current = null;
        syncStickyOverlayState();
      });
    };

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(scheduleSync);

    resizeObserver?.observe(container);
    resizeObserver?.observe(mainTable);

    if (scrollParent && scrollParent !== window) {
      scrollParent.addEventListener('scroll', scheduleSync, { passive: true });
    }

    if (scrollParent === window) {
      window.addEventListener('scroll', scheduleSync, { passive: true });
    }

    if (scrollParent !== container) {
      container.addEventListener('scroll', scheduleSync, { passive: true });
    }
    window.addEventListener('resize', scheduleSync);

    scheduleSync();

    return () => {
      if (stickyRafRef.current !== null) {
        cancelAnimationFrame(stickyRafRef.current);
      }
      resizeObserver?.disconnect();
      if (scrollParent && scrollParent !== window) {
        scrollParent.removeEventListener('scroll', scheduleSync);
      }
      if (scrollParent === window) {
        window.removeEventListener('scroll', scheduleSync);
      }
      if (scrollParent !== container) {
        container.removeEventListener('scroll', scheduleSync);
      }
      window.removeEventListener('resize', scheduleSync);
    };
  }, [
    columnSizing,
    density,
    enableCollapsibleRows,
    expandedRowIds,
    isSelectionVisible,
    rowItems,
    stickyExpandedRowsEnabled,
    stickyHeaderOverlayEnabled,
    visibleColumns.length,
  ]);

  const renderColumnHeader = (column: ColumnDef<T>) => {
    const isSortable = column.sortable === true;
    const isSorted = sort?.columnId === column.id;
    const sortDirection = isSorted ? sort?.direction : undefined;
    const showMenu = column.showMenu !== false;
    const pin = getColumnPin(column.id);

    const menuActions = [
      {
        key: 'sort-asc',
        label: 'Sort ascending',
        onSelect: () => setSort({ columnId: column.id, direction: 'asc' }),
        disabled: !isSortable,
      },
      {
        key: 'sort-desc',
        label: 'Sort descending',
        onSelect: () => setSort({ columnId: column.id, direction: 'desc' }),
        disabled: !isSortable,
      },
      {
        key: 'pin-left',
        label: 'Pin left',
        onSelect: () =>
          setColumnPinning({ ...columnPinning, [column.id]: 'left' }),
        disabled: pin === 'left',
      },
      {
        key: 'pin-right',
        label: 'Pin right',
        onSelect: () =>
          setColumnPinning({ ...columnPinning, [column.id]: 'right' }),
        disabled: pin === 'right',
      },
      {
        key: 'pin-none',
        label: 'Unpin',
        onSelect: () =>
          setColumnPinning({ ...columnPinning, [column.id]: 'none' }),
        disabled: pin === 'none',
      },
      {
        key: 'hide',
        label: 'Hide column',
        onSelect: () =>
          setColumnVisibility({ ...columnVisibility, [column.id]: false }),
      },
      {
        key: 'manage',
        label: 'Manage columns',
        onSelect: (anchor?: HTMLElement | null) =>
          handleManageColumns(column.id, anchor),
      },
    ];

    return (
      <div className={styles.headerContent}>
        <div className={styles.headerLabel}>
          {isSortable ? (
            <TableSortLabel
              active={Boolean(isSorted)}
              direction={sortDirection ?? 'asc'}
              onClick={() =>
                setSort({
                  columnId: column.id,
                  direction:
                    isSorted && sortDirection === 'asc' ? 'desc' : 'asc',
                })
              }
            >
              <span className={styles.headerLabelText}>{column.header}</span>
            </TableSortLabel>
          ) : (
            <span className={styles.headerLabelText}>{column.header}</span>
          )}
        </div>
        {showMenu ? (
          <div
            className={clsx(
              styles.menuSlot,
              openMenuId === column.id && styles.menuSlotOpen,
            )}
          >
            <OptionsMenu
              ariaLabel={`${column.id} column options`}
              actions={menuActions}
              onOpen={(anchor) => {
                setMenuAnchorEl(anchor);
                setOpenMenuId(column.id);
              }}
              onClose={() => {
                if (manageMenuIdRef.current !== column.id) {
                  setOpenMenuId(null);
                }
              }}
            />
          </div>
        ) : null}
      </div>
    );
  };

  const renderDataCells = (
    row: T,
    rowIndex: number,
    rowId: string,
    columnsList: ColumnDef<T>[] = visibleColumns,
    offsets = columnOffsets,
  ) => {
    const isSelected = selectedRowIds.includes(rowId);
    const isRowExpandable =
      enableCollapsibleRows &&
      (isRowCollapsible ? isRowCollapsible(row) : hasSubRowsForRow(row));
    const isRowExpanded = isRowExpandable && expandedRowIds.includes(rowId);

    const cells: React.ReactNode[] = [];

    if (isSelectionVisible) {
      cells.push(
        <TableCell
          key={`${rowId}-select`}
          className={clsx(styles.cell, styles.selectionCell)}
          style={{
            width: SELECTION_COLUMN_WIDTH,
            minWidth: SELECTION_COLUMN_WIDTH,
            maxWidth: SELECTION_COLUMN_WIDTH,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <TableSelectionCheckbox
            name={`${tableId}-row-select-${rowId}`}
            checked={isSelected}
            onChange={() => toggleRowSelection(rowId)}
          />
        </TableCell>,
      );
    }

    if (enableCollapsibleRows) {
      cells.push(
        <TableCell
          key={`${rowId}-expand`}
          className={clsx(styles.cell, isRowExpandable && styles.expandCell)}
          style={{
            width: EXPAND_COLUMN_WIDTH,
            minWidth: EXPAND_COLUMN_WIDTH,
            maxWidth: EXPAND_COLUMN_WIDTH,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {isRowExpandable ? (
            <SecondaryButton
              variant="mini"
              ariaLabel={isRowExpanded ? 'Collapse row' : 'Expand row'}
              onClick={() => toggleRowExpanded(rowId)}
            >
              <ChevronIcon
                direction={isRowExpanded ? 'down' : 'right'}
                size={14}
              />
            </SecondaryButton>
          ) : null}
        </TableCell>,
      );
    }

    columnsList.forEach((column) => {
      const width = getColumnWidth(column, columnSizing);
      const pin = getColumnPin(column.id);
      const isPinned = pin !== 'none';
      const leftOffset = offsets.leftOffsets[column.id];
      const rightOffset = offsets.rightOffsets[column.id];
      const cellContent =
        column.cell?.(row, rowIndex) ?? column.accessor?.(row) ?? null;

      cells.push(
        <TableCell
          key={`${rowId}-${column.id}`}
          className={clsx(styles.cell, isPinned && styles.stickyCell)}
          align={column.align ?? 'left'}
          style={{
            width,
            minWidth: column.minWidth ?? width,
            maxWidth: column.maxWidth,
            left: pin === 'left' ? leftOffset : undefined,
            right: pin === 'right' ? rightOffset : undefined,
          }}
        >
          {cellContent}
        </TableCell>,
      );
    });

    return cells;
  };

  const buildSubRowCells = (row: T, columnsList: ColumnDef<T>[]) => {
    const cellContent =
      renderRowSubCells?.(row) ??
      columnsList.map(
        (column) => column.cell?.(row, 0) ?? column.accessor?.(row) ?? null,
      );
    const headerContent = getSubRowHeaders(columnsList);
    const columnCount = columnsList.length;
    const paddedCells = [
      ...cellContent,
      ...Array(Math.max(0, columnCount - cellContent.length)).fill(null),
    ];
    const paddedHeaders = [
      ...headerContent,
      ...Array(Math.max(0, columnCount - headerContent.length)).fill(null),
    ];

    return { paddedCells, paddedHeaders };
  };

  const renderSubRowCells = (
    row: T,
    columnsList: ColumnDef<T>[],
    subRowId?: string,
  ) => {
    const { paddedCells } = buildSubRowCells(row, columnsList);

    const cells: React.ReactNode[] = [];

    if (isSelectionVisible) {
      cells.push(
        <TableCell
          key="sub-select"
          className={clsx(styles.cell, styles.selectionCell)}
        >
          {isSubSelectionVisible ? (
            <TableSelectionCheckbox
              name={`${tableId}-sub-select-${subRowId ?? 'row'}`}
              checked={Boolean(
                subRowId && selectedSubRowIds.includes(subRowId),
              )}
              onChange={() => {
                if (subRowId) {
                  toggleSubRowSelection(subRowId);
                }
              }}
            />
          ) : null}
        </TableCell>,
      );
    }

    if (enableCollapsibleRows) {
      cells.push(<TableCell key="sub-expand" className={styles.cell} />);
    }

    const offsets = getColumnOffsetsFor(columnsList);
    columnsList.forEach((column, colIndex) => {
      const width = getColumnWidth(column, columnSizing);
      const pin = getColumnPin(column.id);
      const isPinned = pin !== 'none';
      const leftOffset = offsets.leftOffsets[column.id];
      const rightOffset = offsets.rightOffsets[column.id];

      cells.push(
        <TableCell
          key={`sub-${column.id}`}
          className={clsx(styles.cell, isPinned && styles.stickyCell)}
          align={column.align ?? 'left'}
          style={{
            width,
            minWidth: column.minWidth ?? width,
            maxWidth: column.maxWidth,
            left: pin === 'left' ? leftOffset : undefined,
            right: pin === 'right' ? rightOffset : undefined,
          }}
        >
          {paddedCells[colIndex]}
        </TableCell>,
      );
    });

    return cells;
  };

  const renderSubRowHeaderCells = (
    row: T,
    columnsList: ColumnDef<T>[],
    parentRowId?: string,
    subRowIds: string[] = [],
  ) => {
    const { paddedHeaders } = buildSubRowCells(row, columnsList);
    const cells: React.ReactNode[] = [];

    if (isSelectionVisible) {
      cells.push(
        <TableCell
          key="sub-header-parent-select"
          className={clsx(styles.cell, styles.selectionCell)}
          onClick={(event) => event.stopPropagation()}
        >
          {isSubSelectionVisible ? (
            <TableSelectionCheckbox
              name={`${tableId}-sub-header-${parentRowId ?? 'row'}`}
              checked={
                subRowIds.length > 0 &&
                subRowIds.every((id) => selectedSubRowIds.includes(id))
              }
              onChange={() => toggleAllSubRows(subRowIds)}
            />
          ) : null}
        </TableCell>,
      );
    }

    if (enableCollapsibleRows) {
      cells.push(<TableCell key="sub-header-expand" className={styles.cell} />);
    }

    const offsets = getColumnOffsetsFor(columnsList);
    columnsList.forEach((column, colIndex) => {
      const width = getColumnWidth(column, columnSizing);
      const pin = getColumnPin(column.id);
      const isPinned = pin !== 'none';
      const leftOffset = offsets.leftOffsets[column.id];
      const rightOffset = offsets.rightOffsets[column.id];

      cells.push(
        <TableCell
          key={`sub-header-${column.id}`}
          className={clsx(styles.cell, isPinned && styles.stickyCell)}
          align={column.align ?? 'left'}
          style={{
            width,
            minWidth: column.minWidth ?? width,
            maxWidth: column.maxWidth,
            left: pin === 'left' ? leftOffset : undefined,
            right: pin === 'right' ? rightOffset : undefined,
          }}
        >
          <span className={styles.subCellHeader}>
            {paddedHeaders[colIndex]}
          </span>
        </TableCell>,
      );
    });

    return cells;
  };

  const renderSubRowCellsForVirtual = (
    row: T,
    columnsList: ColumnDef<T>[],
    subRowId?: string,
  ) => {
    const { paddedCells, paddedHeaders } = buildSubRowCells(row, columnsList);
    const subHeaderEnabled =
      renderRowSubHeader === undefined || renderRowSubHeader.length > 0;
    const cells: React.ReactNode[] = [];

    if (isSelectionVisible) {
      cells.push(
        <TableCell
          key="sub-select"
          className={clsx(styles.cell, styles.selectionCell)}
          onClick={(event) => event.stopPropagation()}
        >
          {isSubSelectionVisible ? (
            <TableSelectionCheckbox
              name={`${tableId}-sub-select-${subRowId ?? 'row'}`}
              checked={Boolean(
                subRowId && selectedSubRowIds.includes(subRowId),
              )}
              onChange={() => {
                if (subRowId) {
                  toggleSubRowSelection(subRowId);
                }
              }}
            />
          ) : null}
        </TableCell>,
      );
    }

    if (enableCollapsibleRows) {
      cells.push(<TableCell key="sub-expand" className={styles.cell} />);
    }

    const offsets = getColumnOffsetsFor(columnsList);
    columnsList.forEach((column, colIndex) => {
      const width = getColumnWidth(column, columnSizing);
      const pin = getColumnPin(column.id);
      const isPinned = pin !== 'none';
      const leftOffset = offsets.leftOffsets[column.id];
      const rightOffset = offsets.rightOffsets[column.id];

      cells.push(
        <TableCell
          key={`sub-${column.id}`}
          className={clsx(styles.cell, isPinned && styles.stickyCell)}
          align={column.align ?? 'left'}
          style={{
            width,
            minWidth: column.minWidth ?? width,
            maxWidth: column.maxWidth,
            left: pin === 'left' ? leftOffset : undefined,
            right: pin === 'right' ? rightOffset : undefined,
          }}
        >
          <div className={styles.subCell}>
            {subHeaderEnabled ? (
              <span className={styles.subCellHeader}>
                {paddedHeaders[colIndex]}
              </span>
            ) : null}
            <span className={styles.subCellValue}>{paddedCells[colIndex]}</span>
          </div>
        </TableCell>,
      );
    });

    return cells;
  };

  const shouldNestSubRow = useCallback(
    (columnsList: ColumnDef<T>[]) => columnsList.length > visibleColumns.length,
    [visibleColumns.length],
  );

  const getSubRowHeaders = useCallback(
    (columnsList: ColumnDef<T>[]) => {
      if (renderRowSubHeader && renderRowSubHeader.length > 0) {
        return renderRowSubHeader;
      }
      return columnsList.map((col) => col.header ?? '');
    },
    [renderRowSubHeader],
  );

  const getSubRowCells = useCallback(
    (row: T, columnsList: ColumnDef<T>[]) => {
      const cells =
        renderRowSubCells?.(row) ??
        columnsList.map(
          (column) => column.cell?.(row, 0) ?? column.accessor?.(row) ?? null,
        );
      return [
        ...cells,
        ...Array(Math.max(0, columnsList.length - cells.length)).fill(null),
      ];
    },
    [renderRowSubCells],
  );

  const hasSubRowCells = Boolean(
    renderRowSubCells ||
      (renderRowSubColumns && renderRowSubColumns.length > 0),
  );

  const renderNestedSubTable = (
    rows: { row: T; subRowId: string }[],
    columnsList: ColumnDef<T>[],
    showHeader: boolean,
    subRowIds: string[],
    parentRowId?: string,
  ) => {
    const headers = getSubRowHeaders(columnsList);

    return (
      <table className={styles.subTable}>
        {showHeader && headers.length > 0 ? (
          <thead>
            <tr>
              {isSelectionVisible ? (
                <th
                  className={clsx(
                    styles.subTableHeaderCell,
                    styles.selectionCell,
                    styles.subTableSelectionCell,
                  )}
                  onClick={(event) => event.stopPropagation()}
                >
                  {isSubSelectionVisible ? (
                    <TableSelectionCheckbox
                      name={`${tableId}-sub-header-${parentRowId ?? 'row'}`}
                      checked={
                        subRowIds.length > 0 &&
                        subRowIds.every((id) => selectedSubRowIds.includes(id))
                      }
                      onChange={() => toggleAllSubRows(subRowIds)}
                    />
                  ) : null}
                </th>
              ) : null}
              {headers.map((header, idx) => (
                <th
                  key={`sub-header-${idx}`}
                  className={styles.subTableHeaderCell}
                >
                  <span className={styles.subCellHeader}>{header}</span>
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {rows.map(({ row, subRowId }, rowIndex) => {
            const cells = getSubRowCells(row, columnsList);
            return (
              <tr key={`sub-row-${rowIndex}`}>
                {isSelectionVisible ? (
                  <td
                    className={clsx(
                      styles.subTableCell,
                      styles.selectionCell,
                      styles.subTableSelectionCell,
                    )}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {isSubSelectionVisible ? (
                      <TableSelectionCheckbox
                        name={`${tableId}-sub-select-${subRowId ?? 'row'}`}
                        checked={Boolean(
                          subRowId && selectedSubRowIds.includes(subRowId),
                        )}
                        onChange={() => {
                          if (subRowId) {
                            toggleSubRowSelection(subRowId);
                          }
                        }}
                      />
                    ) : null}
                  </td>
                ) : null}
                {cells.map((cell, idx) => (
                  <td
                    key={`sub-cell-${rowIndex}-${idx}`}
                    className={styles.subTableCell}
                  >
                    <span className={styles.subCellValue}>{cell}</span>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderRowItem = (item: RowItem<T>, index: number) => {
    if (item.type === 'section') {
      return (
        <TableRow
          key={`section-${item.section.id}`}
          className={styles.sectionRow}
        >
          <TableCell
            colSpan={getColSpanForSection(item.section.id)}
            className={clsx(styles.cell, styles.expandCell)}
          >
            <div className={styles.sectionLabel}>
              {item.section.collapsible ? (
                <SecondaryButton
                  variant="mini"
                  ariaLabel={
                    item.expanded ? 'Collapse section' : 'Expand section'
                  }
                  onClick={() => toggleSectionExpanded(item.section.id)}
                >
                  <ChevronIcon
                    direction={item.expanded ? 'down' : 'right'}
                    size={14}
                  />
                </SecondaryButton>
              ) : null}
              {item.section.renderSectionHeader
                ? item.section.renderSectionHeader(item.section, item.expanded)
                : item.section.label}
            </div>
          </TableCell>
        </TableRow>
      );
    }

    if (item.type === 'detail') {
      if (hasSubRowCells) {
        const columnsForRow =
          renderRowSubColumns ?? getSectionColumns(item.sectionId);
        const subHeaderEnabled =
          renderRowSubHeader === undefined || renderRowSubHeader.length > 0;
        const showSubHeader = subHeaderEnabled && item.detailIndex === 0;
        const subRowItems = subRowItemsByParentId[item.parentRowId] ?? [];
        const subRowIds = subRowItems.map((subRow) => subRow.subRowId);
        if (shouldNestSubRow(columnsForRow)) {
          if (item.detailIndex !== 0) {
            return null;
          }
          return (
            <TableRow
              key={`detail-${item.rowId}`}
              data-row-type="detail"
              data-parent-row-id={item.parentRowId}
            >
              <TableCell
                colSpan={visibleColumns.length + extraCellCount}
                className={styles.detailRowCell}
              >
                <div className={styles.detailContent}>
                  {renderNestedSubTable(
                    subRowItems,
                    columnsForRow,
                    showSubHeader,
                    subRowIds,
                    item.parentRowId,
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        }
        return (
          <>
            {showSubHeader ? (
              <TableRow
                key={`detail-${item.rowId}-header`}
                className={styles.subHeaderRow}
                data-row-type="detail"
                data-parent-row-id={item.parentRowId}
              >
                {renderSubRowHeaderCells(
                  item.row,
                  columnsForRow,
                  item.parentRowId,
                  subRowIds,
                )}
              </TableRow>
            ) : null}
            <TableRow
              key={`detail-${item.rowId}`}
              className={styles.subRow}
              data-row-type="detail"
              data-parent-row-id={item.parentRowId}
            >
              {renderSubRowCells(item.row, columnsForRow, item.subRowId)}
            </TableRow>
          </>
        );
      }

      return (
        <TableRow
          key={`detail-${item.rowId}`}
          data-row-type="detail"
          data-parent-row-id={item.parentRowId}
        >
          <TableCell
            colSpan={visibleColumns.length + extraCellCount}
            className={styles.detailRowCell}
          >
            <Collapse in timeout="auto" unmountOnExit>
              <div className={styles.detailContent}>
                {renderRowSubComponent ? renderRowSubComponent(item.row) : null}
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      );
    }

    const rowId = item.rowId;
    const isRowExpandable =
      enableCollapsibleRows &&
      (isRowCollapsible
        ? isRowCollapsible(item.row)
        : hasSubRowsForRow(item.row));
    const isPinnedSourceRow =
      stickyExpandedRowsEnabled && hiddenPinnedSourceRowId === rowId;
    const columnsForRow = getSectionColumns(item.sectionId);
    const offsets = getColumnOffsetsFor(columnsForRow);

    return (
      <TableRow
        key={rowId}
        hover
        className={clsx(
          styles.pinnedSourceRow,
          isPinnedSourceRow && styles.pinnedSourceRowHidden,
        )}
        data-row-type="data"
        data-row-id={rowId}
        onClick={() => onRowClick?.(item.row)}
      >
        {renderDataCells(item.row, index, rowId, columnsForRow, offsets)}
      </TableRow>
    );
  };

  const headerRow = (
    <TableRow className={styles.headerRow}>
      {isSelectionVisible ? (
        <TableCell
          className={clsx(styles.headCell, styles.selectionCell)}
          style={{
            width: SELECTION_COLUMN_WIDTH,
            minWidth: SELECTION_COLUMN_WIDTH,
            maxWidth: SELECTION_COLUMN_WIDTH,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {selectionMode === 'multiple' ? (
            <TableSelectionCheckbox
              name={`${tableId}-select-all`}
              checked={allSelected}
              onChange={() => toggleAllRows(rowIds)}
            />
          ) : null}
        </TableCell>
      ) : null}
      {enableCollapsibleRows ? (
        <TableCell
          className={clsx(styles.headCell, styles.expandHeaderCell)}
          style={{
            width: EXPAND_COLUMN_WIDTH,
            minWidth: EXPAND_COLUMN_WIDTH,
            maxWidth: EXPAND_COLUMN_WIDTH,
          }}
        />
      ) : null}
      {visibleColumns.map((column) => {
        const width = getColumnWidth(column, columnSizing);
        const pin = getColumnPin(column.id);
        const isPinned = pin !== 'none';
        const leftOffset = columnOffsets.leftOffsets[column.id];
        const rightOffset = columnOffsets.rightOffsets[column.id];
        return (
          <TableCell
            key={column.id}
            className={clsx(styles.headCell, isPinned && styles.stickyCell)}
            align={column.align ?? 'left'}
            style={{
              width,
              minWidth: column.minWidth ?? width,
              maxWidth: column.maxWidth,
              left: pin === 'left' ? leftOffset : undefined,
              right: pin === 'right' ? rightOffset : undefined,
            }}
          >
            {renderColumnHeader(column)}
            <div
              className={styles.resizeHandle}
              onMouseDown={(event) => startResize(column, event)}
              role="presentation"
            />
          </TableCell>
        );
      })}
    </TableRow>
  );

  const getSkeletonRowCount = () => {
    const base = typeof pageSize === 'number' && pageSize > 0 ? pageSize : 4;
    const count =
      typeof skeletonRowCount === 'number' && skeletonRowCount > 0
        ? skeletonRowCount
        : base;
    return Math.max(1, Math.min(count, 10));
  };

  const renderSkeletonRows = (rowCount: number) =>
    Array.from({ length: rowCount }).map((_, rowIndex) => (
      <TableRow key={`skeleton-${rowIndex}`}>
        {isSelectionVisible ? (
          <TableCell className={clsx(styles.cell, styles.selectionCell)}>
            <Skeleton variant="rectangular" width={16} height={16} />
          </TableCell>
        ) : null}
        {enableCollapsibleRows ? (
          <TableCell className={styles.cell}>
            <Skeleton variant="circular" width={16} height={16} />
          </TableCell>
        ) : null}
        {visibleColumns.map((column) => {
          const width = getColumnWidth(column, columnSizing);
          return (
            <TableCell
              key={`skeleton-${rowIndex}-${column.id}`}
              className={clsx(
                styles.cell,
                getColumnPin(column.id) !== 'none' && styles.stickyCell,
              )}
              align={column.align ?? 'left'}
              style={{
                width,
                minWidth: column.minWidth ?? width,
                maxWidth: column.maxWidth,
              }}
            >
              <Skeleton variant="text" height={20} />
            </TableCell>
          );
        })}
      </TableRow>
    ));

  const renderStickyHeaderOverlay = () => {
    if (!stickyHeaderOverlayEnabled || !isHeaderOverlayPinned) {
      return null;
    }

    return (
      <div
        className={styles.stickyHeaderOverlay}
        style={{ top: overlayTop, left: overlayLeft, width: overlayWidth }}
        aria-hidden
      >
        <div
          className={styles.overlayInner}
          style={{
            transform: `translateX(-${overlayScrollLeft}px)`,
            width: overlayTableWidth > 0 ? `${overlayTableWidth}px` : undefined,
          }}
        >
          <Table
            className={clsx(
              styles.table,
              styles.pinnedOverlayTable,
              densityClass,
              className,
            )}
          >
            <TableHead>{headerRow}</TableHead>
          </Table>
        </div>
      </div>
    );
  };

  const renderPinnedExpandedRowOverlay = () => {
    if (
      !stickyExpandedRowsEnabled ||
      !isHeaderOverlayPinned ||
      !activePinnedRowId
    ) {
      return null;
    }

    const pinnedRow = rowDataById[activePinnedRowId];
    if (!pinnedRow) {
      return null;
    }

    const columnsForRow = getSectionColumns(pinnedRow.sectionId);
    const offsets = getColumnOffsetsFor(columnsForRow);

    return (
      <div
        className={styles.pinnedExpandedRowOverlay}
        style={{
          top: overlayTop + overlayHeaderHeight,
          left: overlayLeft,
          width: overlayWidth,
        }}
        aria-hidden
      >
        <div
          className={styles.overlayInner}
          style={{
            transform: `translateX(-${overlayScrollLeft}px)`,
            width: overlayTableWidth > 0 ? `${overlayTableWidth}px` : undefined,
          }}
        >
          <Table
            className={clsx(
              styles.table,
              styles.pinnedOverlayTable,
              densityClass,
              className,
            )}
          >
            <TableBody>
              <TableRow className={styles.overlayPinnedRow}>
                {renderDataCells(
                  pinnedRow.row,
                  pinnedRow.rowIndex,
                  activePinnedRowId,
                  columnsForRow,
                  offsets,
                )}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    );
  };

  const renderClassicTable = () => (
    <TableContainer ref={tableContainerRef} className={styles.tableContainer}>
      {renderStickyHeaderOverlay()}
      {renderPinnedExpandedRowOverlay()}
      <Table
        ref={mainTableRef}
        aria-label={`${tableId}-table`}
        className={clsx(styles.table, densityClass, className)}
      >
        <TableHead
          className={clsx(
            stickyHeaderOverlayEnabled &&
              isHeaderOverlayPinned &&
              styles.ghostTableHead,
          )}
        >
          {headerRow}
        </TableHead>
        <TableBody>
          {isLoading ? (
            renderSkeletonRows(getSkeletonRowCount())
          ) : rowItems.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={visibleColumns.length + extraCellCount}
                className={styles.cell}
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rowItems.map((item, index) => renderRowItem(item, index))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderVirtualRow = (item: RowItem<T>, index: number) => {
    if (item.type === 'section') {
      return (
        <TableCell
          colSpan={getColSpanForSection(item.section.id)}
          className={clsx(styles.cell, styles.sectionRow)}
        >
          <div className={styles.sectionLabel}>
            {item.section.collapsible ? (
              <SecondaryButton
                variant="mini"
                ariaLabel={
                  item.expanded ? 'Collapse section' : 'Expand section'
                }
                onClick={() => toggleSectionExpanded(item.section.id)}
              >
                <ChevronIcon
                  direction={item.expanded ? 'down' : 'right'}
                  size={14}
                />
              </SecondaryButton>
            ) : null}
            {item.section.renderSectionHeader
              ? item.section.renderSectionHeader(item.section, item.expanded)
              : item.section.label}
          </div>
        </TableCell>
      );
    }

    if (item.type === 'detail') {
      if (renderRowSubCells) {
        const columnsForRow =
          renderRowSubColumns ?? getSectionColumns(item.sectionId);
        const subHeaderEnabled =
          renderRowSubHeader === undefined || renderRowSubHeader.length > 0;
        const showSubHeader = subHeaderEnabled && item.detailIndex === 0;
        const subRowItems = subRowItemsByParentId[item.parentRowId] ?? [];
        const subRowIds = subRowItems.map((subRow) => subRow.subRowId);

        if (shouldNestSubRow(columnsForRow)) {
          if (item.detailIndex !== 0) {
            return null;
          }
          return (
            <TableCell
              colSpan={visibleColumns.length + extraCellCount}
              className={styles.detailRowCell}
            >
              <div className={styles.detailContent}>
                {renderNestedSubTable(
                  subRowItems,
                  columnsForRow,
                  showSubHeader,
                  subRowIds,
                  item.parentRowId,
                )}
              </div>
            </TableCell>
          );
        }
        return renderSubRowCellsForVirtual(
          item.row,
          columnsForRow,
          item.subRowId,
        );
      }

      return (
        <TableCell
          colSpan={visibleColumns.length + extraCellCount}
          className={styles.detailRowCell}
        >
          <Collapse in timeout="auto" unmountOnExit>
            <div className={styles.detailContent}>
              {renderRowSubComponent ? renderRowSubComponent(item.row) : null}
            </div>
          </Collapse>
        </TableCell>
      );
    }

    const columnsForRow = getSectionColumns(item.sectionId);
    const offsets = getColumnOffsetsFor(columnsForRow);
    return renderDataCells(item.row, index, item.rowId, columnsForRow, offsets);
  };

  const getRowItemKey = (index: number, item: RowItem<T>) => {
    if (item.type === 'section') {
      return `section-${item.section.id}`;
    }
    if (item.type === 'detail') {
      return `detail-${item.rowId}`;
    }
    return item.rowId ?? `row-${index}`;
  };

  const VirtuosoScroller = useMemo(() => {
    const Scroller = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
      (props, ref) => <Box {...props} ref={ref} />,
    );
    Scroller.displayName = 'TableBaseImplVirtuosoScroller';
    return Scroller;
  }, []);

  const VirtuosoTableBody = useMemo(() => {
    const Body = forwardRef<
      HTMLTableSectionElement,
      HTMLAttributes<HTMLTableSectionElement>
    >((props, ref) => <TableBody {...props} ref={ref} />);
    Body.displayName = 'TableBaseImplVirtuosoBody';
    return Body;
  }, []);

  const VirtuosoTableHead = useCallback(function VirtuosoTableHead(
    props: ComponentProps<typeof TableHead>,
  ) {
    return (
      <TableHead {...props} style={{ position: 'sticky', top: 0, zIndex: 5 }} />
    );
  }, []);

  const VirtuosoTable = useCallback(
    (props: ComponentProps<typeof Table>) => (
      <Table
        {...props}
        className={clsx(styles.table, densityClass, className)}
      />
    ),
    [className, densityClass],
  );

  const virtuosoComponents = useMemo(
    () => ({
      Scroller: VirtuosoScroller,
      Table: VirtuosoTable,
      TableHead: VirtuosoTableHead,
      TableRow,
      TableBody: VirtuosoTableBody,
    }),
    [VirtuosoScroller, VirtuosoTable, VirtuosoTableBody, VirtuosoTableHead],
  );

  const renderVirtualizedTable = () => {
    if (virtualizedHeight === 'auto') {
      return (
        <TableContainer
          ref={tableContainerRef}
          className={styles.tableContainer}
        >
          <Table
            ref={mainTableRef}
            stickyHeader
            aria-label={`${tableId}-table`}
            className={clsx(styles.table, densityClass, className)}
          >
            <TableHead>{headerRow}</TableHead>
            <TableBody>
              {isLoading ? (
                renderSkeletonRows(getSkeletonRowCount())
              ) : rowItems.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length + extraCellCount}
                    className={styles.cell}
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                rowItems.map((item, index) => renderRowItem(item, index))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    if (rowItems.length === 0) {
      return (
        <TableContainer className={styles.tableContainer}>
          <Table
            stickyHeader
            aria-label={`${tableId}-table`}
            className={clsx(styles.table, densityClass, className)}
          >
            <TableHead>{headerRow}</TableHead>
            <TableBody>
              {isLoading ? (
                renderSkeletonRows(getSkeletonRowCount())
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length + extraCellCount}
                    className={styles.cell}
                  >
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    const containerStyle = { height: virtualizedHeight };

    return (
      <TableContainer className={styles.tableContainer} style={containerStyle}>
        <TableVirtuoso<RowItem<T>>
          data={rowItems}
          endReached={hasMore ? onLoadMore : undefined}
          components={virtuosoComponents}
          fixedHeaderContent={() => headerRow}
          computeItemKey={getRowItemKey}
          itemContent={(index, item) => renderVirtualRow(item, index)}
          style={{ height: '100%' }}
        />
      </TableContainer>
    );
  };

  const visibleManagedColumnCount = useMemo(
    () =>
      orderedColumns.filter((column) => columnVisibility[column.id] !== false)
        .length,
    [columnVisibility, orderedColumns],
  );

  const handleColumnVisibilityChange = useCallback(
    (columnId: string, checked: boolean) => {
      const isCurrentlyVisible = columnVisibility[columnId] !== false;

      // Keep at least one visible column to avoid locking users out of controls.
      if (!checked && isCurrentlyVisible && visibleManagedColumnCount <= 1) {
        return;
      }

      setColumnVisibility({
        ...columnVisibility,
        [columnId]: checked,
      });
    },
    [columnVisibility, setColumnVisibility, visibleManagedColumnCount],
  );

  const handleHideAllColumns = useCallback(() => {
    if (orderedColumns.length === 0) {
      return;
    }

    const [firstColumn, ...otherColumns] = orderedColumns;
    setColumnVisibility({
      [firstColumn.id]: true,
      ...otherColumns.reduce<Record<string, boolean>>((acc, col) => {
        acc[col.id] = false;
        return acc;
      }, {}),
    });
  }, [orderedColumns, setColumnVisibility]);

  return (
    <div
      ref={wrapperRef}
      className={styles.tableWrapper}
      style={tableWrapperStyle}
    >
      {paginationMode === 'infinite'
        ? renderVirtualizedTable()
        : renderClassicTable()}
      {paginationMode === 'classic' && showPagination ? (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) =>
            setPageSize(Number(event.target.value))
          }
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      ) : null}
      <Popover
        open={manageOpen}
        anchorEl={manageAnchorEl}
        onClose={handleManageClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className={styles.manageColumns}>
          <div className={styles.manageHeader}>Manage columns</div>
          <div className={styles.manageList}>
            {orderedColumns.map((column) => {
              const label =
                typeof column.header === 'string' ? column.header : column.id;
              return (
                <div key={column.id} className={styles.manageItem}>
                  <SmartInput
                    type="checkbox"
                    name={`${tableId}-column-${column.id}`}
                    label={label}
                    checked={columnVisibility[column.id] !== false}
                    readOnly={
                      columnVisibility[column.id] !== false &&
                      visibleManagedColumnCount <= 1
                    }
                    onChange={(checked) =>
                      handleColumnVisibilityChange(column.id, checked)
                    }
                  />
                </div>
              );
            })}
          </div>
          <div className={styles.manageFooter}>
            <InlineButton
              ariaLabel="Hide all columns"
              onClick={handleHideAllColumns}
            >
              Hide all
            </InlineButton>
            <InlineButton
              ariaLabel="Show all columns"
              onClick={() =>
                setColumnVisibility(
                  orderedColumns.reduce<Record<string, boolean>>((acc, col) => {
                    acc[col.id] = true;
                    return acc;
                  }, {}),
                )
              }
            >
              Show all
            </InlineButton>
          </div>
        </div>
      </Popover>
    </div>
  );
}

export type {
  ColumnDef,
  ColumnOrder,
  ColumnPin,
  ColumnSizing,
  ColumnVisibility,
  TableBaseImplProps,
  Density,
  SelectionMode,
  SortDirection,
  TablePaginationMode,
  TableSection,
  TableSort,
} from './TableBaseImpl.types';
